// The library id is the numeric path segment after `/libraries/`. It is
// followed by more path (`/articles/...`, `/{doi}`), a query string, a
// fragment, or the end of the URL — accept any of those terminators so every
// real-world Nomad link shape resolves.
//
// The extension links to two host shapes: the original `libkey.io/libraries/`
// and the newer versioned `nomad-api.thirdiron.com/v{n}/libraries/` API host.
const LIBKEY_LIBRARY_ID = /(?:libkey\.io|nomad-api\.thirdiron\.com\/v\d+)\/libraries\/(\d+)(?:[/?#]|$)/;

/**
 * How long to keep watching the DOM for a Nomad button before giving up. The
 * extension injects buttons only after its per-DOI API round-trips resolve,
 * so a populated study list can take several seconds to yield one.
 */
export const HARVEST_TIMEOUT_MS = 15_000;

/**
 * Read a LibKey library id out of any LibKey Nomad button currently in the
 * DOM. The extension's buttons link to server-provided
 * `https://libkey.io/libraries/{libraryId}/...` or
 * `https://nomad-api.thirdiron.com/v{n}/libraries/{libraryId}/...` URLs, so
 * the id is readable from the page even though the extension's network traffic
 * isn't.
 *
 * Scoped to `.nomad-container` (the extension's injected wrapper) so we never
 * pick up Covidence's own native LibkeyLink links — those use the same URL
 * shape but only render once a library is already configured.
 */
export function harvestFromDom(root: ParentNode = document): number | null {
  const anchors = root.querySelectorAll<HTMLAnchorElement>(
    '.nomad-container a[href*="/libraries/"]'
  );
  for (const anchor of anchors) {
    const match = anchor.href.match(LIBKEY_LIBRARY_ID);
    if (match) return Number(match[1]);
  }
  return null;
}

/**
 * Resolve with the first library id a Nomad button exposes, or null if none
 * appears within `timeoutMs`. The extension injects asynchronously, so a
 * button may not exist at mount time — we watch the whole body for additions.
 * Not every reference yields a `libkey.io/libraries/{id}` link (alert and
 * fallback states point elsewhere), which is why we observe rather than
 * sample once.
 */
export function harvestNomadLibraryId(
  timeoutMs = HARVEST_TIMEOUT_MS
): Promise<number | null> {
  return new Promise((resolve) => {
    const immediate = harvestFromDom();
    if (immediate !== null) return resolve(immediate);

    let settled = false;
    const finish = (id: number | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(id);
    };

    const observer = new MutationObserver(() => {
      const id = harvestFromDom();
      if (id !== null) finish(id);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const timer = setTimeout(() => finish(null), timeoutMs);
  });
}
